import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { CircleAlert } from 'lucide-react';
import { useTheme } from '@course-authoring/shared-types';
import {
  type BuildAction,
  type BuildView,
  type ProgressView,
  type RunSummary,
} from '../../api/authorWithAiApi';
import { AssistantAvatar, MessageRow, ResultLinks } from './MessageParts';
import { formatElapsed, waitingSentence } from './threadModel';
import {
  BUILD_STAGE_LABELS,
  FAILURE_COPY,
  GENERATION_COPY,
  LESSON_STAGE_LABELS,
  LESSON_STATUS_LABELS,
  RESULT_COPY,
  UNKNOWN_STAGE_LABEL,
  WAITING_COPY,
} from './authorWithAiCopy';
import { createAuthorWithAiStyles } from './authorWithAiStyles';

const useStyles = () => {
  const { tokens } = useTheme();
  return useMemo(() => createAuthorWithAiStyles(tokens), [tokens]);
};

/** Submits a typed action; resolves to the error (already reported) or null on success. */
export type SubmitAction = (action: BuildAction) => Promise<unknown>;

const stageLabel = (stage: string | null | undefined): string =>
  (stage && (BUILD_STAGE_LABELS[stage] ?? LESSON_STAGE_LABELS[stage])) || UNKNOWN_STAGE_LABEL;

// ── Waiting ─────────────────────────────────────────────────────────────────

/** Transient, never persisted: one honest sentence, a spinner, and elapsed time after 30 s. */
export function WaitingCard({ view, run }: { view: BuildView; run: RunSummary }) {
  const styles = useStyles();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const elapsed = now - new Date(run.createdAt).getTime();
  return (
    <div data-testid="waiting-card">
      <MessageRow role="assistant" initials="">
        <span style={styles.waiting} role="status">
          <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />
          <span>{waitingSentence(view)}</span>
          {elapsed > WAITING_COPY.elapsedAfterMs && <span style={styles.muted}>{formatElapsed(elapsed)}</span>}
        </span>
      </MessageRow>
    </div>
  );
}

// ── Generation ──────────────────────────────────────────────────────────────

const lessonStatusLabel = (lesson: ProgressView['lessons'][number]): string => {
  if (lesson.status === 'running') return LESSON_STAGE_LABELS[lesson.stage ?? ''] ?? UNKNOWN_STAGE_LABEL;
  return LESSON_STATUS_LABELS[lesson.status];
};

/** The building message: estimate, the reported build stage and each lesson's reported stage. */
export function GenerationMessage({ progress }: { progress: ProgressView }) {
  const styles = useStyles();
  const isCourse = progress.lessons.length > 1 || progress.course !== null;
  return (
    <div data-testid="generation-message">
      <MessageRow role="assistant" initials="">
        <p style={styles.paragraph}>{isCourse ? GENERATION_COPY.course : GENERATION_COPY.lesson}</p>
        <span style={styles.waiting} role="status">
          <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" />
          <span>{stageLabel(progress.stage)}</span>
        </span>
        <ul style={styles.progressList}>
          {progress.lessons.map((lesson) => (
            <li key={lesson.outlineLessonId} style={styles.progressRow}>
              <span>{lesson.title}</span>
              <span style={styles.progressStatus}>{lessonStatusLabel(lesson)}</span>
            </li>
          ))}
          {progress.course && (
            <li style={styles.progressRow}>
              <span>{GENERATION_COPY.courseRow}</span>
              <span style={styles.progressStatus}>
                {progress.course.status === 'running' ? (BUILD_STAGE_LABELS.course ?? UNKNOWN_STAGE_LABEL) : LESSON_STATUS_LABELS[progress.course.status]}
              </span>
            </li>
          )}
        </ul>
        <p style={styles.muted}>{GENERATION_COPY.stayOrLeave}</p>
      </MessageRow>
    </div>
  );
}

// ── Failures ────────────────────────────────────────────────────────────────

function NoticeShell({ children, styles }: { children: ReactNode; styles: Record<string, CSSProperties> }) {
  return (
    <div style={{ ...styles.messageRow }}>
      <AssistantAvatar styles={styles} />
      <div style={{ ...styles.noticeCard, flex: 1 }} role="alert">{children}</div>
    </div>
  );
}

/** A failed conversation turn: the safe message and Retry (or "Retry the reply"). */
export function TurnFailureNotice({ run, retry, onSubmit, disabled }: {
  run: RunSummary;
  retry: { runId?: string; replyOnly?: boolean } | null;
  onSubmit: SubmitAction;
  disabled: boolean;
}) {
  const styles = useStyles();
  return (
    <div data-testid="turn-failure">
      <NoticeShell styles={styles}>
        <span style={styles.approvalLine}><CircleAlert size="1em" strokeWidth={1.75} aria-hidden="true" />{FAILURE_COPY.turnFailed}</span>
        {run.error && <p style={styles.paragraph}>{run.error.message}</p>}
        {retry && (
          <div style={styles.actionsRow}>
            <button
              type="button"
              style={{ ...styles.button, ...(disabled ? styles.buttonDisabled : {}) }}
              disabled={disabled}
              onClick={() => void onSubmit({ type: 'retry', runId: run.id, ...(retry.replyOnly ? { replyOnly: true } : {}) })}
            >
              {retry.replyOnly ? FAILURE_COPY.retryReply : FAILURE_COPY.retry}
            </button>
          </div>
        )}
      </NoticeShell>
    </div>
  );
}

/** A settled failed build: message, stage, saved lessons, and exactly the actions the view allows. */
export function BuildFailureNotice({ view, run, onSubmit, onDiscard, disabled }: {
  view: BuildView;
  run: RunSummary;
  onSubmit: SubmitAction;
  onDiscard: () => void;
  disabled: boolean;
}) {
  const styles = useStyles();
  const result = view.results.find((entry) => entry.runId === run.id && entry.current);
  const allows = (type: string) => view.allowedActions.some((action) => action.type === type && (action.runId === undefined || action.runId === run.id));
  const buttonStyle = { ...styles.button, ...(disabled ? styles.buttonDisabled : {}) };
  return (
    <div data-testid="build-failure">
      <NoticeShell styles={styles}>
        <strong>{FAILURE_COPY.buildFailed}</strong>
        {run.error && <p style={styles.paragraph}>{run.error.message}</p>}
        {run.error?.stage && <p style={styles.muted}>{FAILURE_COPY.stage(stageLabel(run.error.stage))}</p>}
        {run.status === 'needs_input' && view.workingState.position.outstanding.length > 0 && (
          <ul style={styles.bulletList}>
            {view.workingState.position.outstanding.map((item, index) => <li key={index}>{item.message}</li>)}
          </ul>
        )}
        {result && result.lessons.length > 0 && (
          <>
            <span style={styles.muted}>{FAILURE_COPY.saved}</span>
            <ResultLinks view={view} result={result} styles={styles} />
          </>
        )}
        <div style={styles.actionsRow}>
          {allows('retry') && <button type="button" style={buttonStyle} disabled={disabled} onClick={() => void onSubmit({ type: 'retry', runId: run.id })}>{FAILURE_COPY.retry}</button>}
          {allows('revise') && <button type="button" style={buttonStyle} disabled={disabled} onClick={() => void onSubmit({ type: 'revise', runId: run.id })}>{FAILURE_COPY.revise}</button>}
          {allows('discard') && <button type="button" style={buttonStyle} disabled={disabled} onClick={onDiscard}>{FAILURE_COPY.discard}</button>}
        </div>
      </NoticeShell>
    </div>
  );
}

/** A generation that went back to planning for input: the outstanding items. */
export function NeedsInputNote({ view }: { view: BuildView }) {
  const styles = useStyles();
  const items = view.workingState.position.outstanding;
  if (items.length === 0) return null;
  return (
    <div data-testid="needs-input">
      <NoticeShell styles={styles}>
        <span>{FAILURE_COPY.needsInput}</span>
        <ul style={styles.bulletList}>{items.map((item, index) => <li key={index}>{item.message}</li>)}</ul>
      </NoticeShell>
    </div>
  );
}

/** Drafts from runs the author later revised, kept apart from the current result. */
export function EarlierDrafts({ view }: { view: BuildView }) {
  const styles = useStyles();
  const retained = view.results.filter((entry) => !entry.current);
  if (retained.length === 0) return null;
  return (
    <details style={styles.card2} data-testid="earlier-drafts">
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{RESULT_COPY.earlierDrafts}</summary>
      {retained.map((result) => <ResultLinks key={result.runId} view={view} result={result} styles={styles} />)}
    </details>
  );
}
